import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { JmapClient } from './lib/jmap.js';
import { CryptoClient } from './lib/crypto.js';
import { createApis, recoveryApiForAuth, totpRequired, totpVerify } from './lib/api.js';
import { useTheme } from './lib/theme.js';
import { useLocalKeystore } from './lib/keystore.js';
import { ToastProvider, useToast } from './components/ui.jsx';
import { AuthShell } from './pages/AuthShell.jsx';
import { Login } from './pages/Login.jsx';
import { Register } from './pages/Register.jsx';
import { Forgot } from './pages/Forgot.jsx';
import { FindAddress } from './pages/FindAddress.jsx';
import { Shell } from './pages/Shell.jsx';

// Persisted session so a refresh does not log the user out.
//  - "Trust this device for 30 days" (remember=true) -> localStorage with a real
//    30-day expiry.
//  - unchecked -> sessionStorage, cleared when the tab/window closes.
// Only the account email + its auth header are stored (never the raw password in
// a separate field); private keys stay in the local keystore (IndexedDB).
const SESSION_KEY = 'km-session';
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
const SESSION = {
  save(data, remember) {
    try {
      sessionStorage.removeItem(SESSION_KEY); localStorage.removeItem(SESSION_KEY);
      const store = remember ? localStorage : sessionStorage;
      store.setItem(SESSION_KEY, JSON.stringify({ ...data, remember: !!remember, exp: remember ? Date.now() + THIRTY_DAYS : 0 }));
    } catch { /* storage unavailable */ }
  },
  load() {
    try {
      const raw = localStorage.getItem(SESSION_KEY) || sessionStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      const s = JSON.parse(raw);
      if (s.exp && Date.now() > s.exp) { this.clear(); return null; } // expired trust window
      return s.email && s.authHeader ? s : null;
    } catch { return null; }
  },
  clear() { try { localStorage.removeItem(SESSION_KEY); sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ } },
};

// Root state machine: login | register | forgot | find | app.
export function App() {
  return <ToastProvider><Root /></ToastProvider>;
}

// Second-factor gate shown after a valid password when the account has TOTP on.
function TotpGate({ email, error, onSubmit, onCancel }) {
  const [code, setCode] = useState('');
  return (
    <form className="km-auth-form" onSubmit={(e) => { e.preventDefault(); onSubmit(code); }}>
      <div><div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.02em' }}>Two-factor authentication</div><div className="km-muted" style={{ fontSize: 14, marginTop: 6, lineHeight: 1.5 }}>Enter the 6-digit code from your authenticator app for {email}.</div></div>
      <label className="km-field">Authenticator code<input className="km-input mono" autoFocus inputMode="numeric" placeholder="000 000" value={code} onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, '').slice(0, 7))} /></label>
      {error && <div style={{ color: 'var(--km-bad)', fontSize: 13 }}>{error}</div>}
      <button className="km-btn" type="submit" disabled={code.replace(/\D/g, '').length !== 6}>Verify and sign in</button>
      <button type="button" className="km-btn-ghost" onClick={onCancel}>Back</button>
    </form>
  );
}

function Root() {
  const theme = useTheme('light');
  const flash = useToast();
  const keystore = useLocalKeystore();
  const [screen, setScreen] = useState({ name: 'login', email: '' });
  const [session, setSession] = useState(null); // { jmap, crypto, email, apis }
  // Start in "restoring" when a persisted session exists, so a refresh goes
  // straight to a splash (never a flash of the login page) while we reconnect.
  const [restoring, setRestoring] = useState(() => !!SESSION.load());
  const [error, setError] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [totpStep, setTotpStep] = useState(null); // { email, authHeader, remember } when 2FA code is required
  const recoveryApi = useMemo(() => recoveryApiForAuth(), []);

  useEffect(() => { recoveryApi.registration().then(setRegistration).catch(() => setRegistration({ enabled: false })); }, [recoveryApi]);

  const go = useCallback((name, extra = {}) => { setError(null); setScreen({ name, ...extra }); }, []);

  // Build a live session from an email + auth header. Shared by interactive login
  // and session restore so both paths produce an identical session object.
  const buildSession = useCallback(async (email, authHeader) => {
    const jmap = new JmapClient({ baseUrl: '', authHeader });
    await jmap.connect(); // validates the credentials against the live server
    const crypto = new CryptoClient();
    const apis = createApis({ jmap, authHeader });
    // Resolve the account's published identity + any device-local private keys.
    let self = await crypto.lookupRecipient(email).catch(() => null);
    let priv = keystore.identity(email); // { kem, sig } private keys, device-bound
    // Auto-provision on first login if the account has no published identity AND no
    // local keys - no admin provisions users one by one. The crypto-service
    // generates the keypair, publishes the public bundle, and returns the private
    // keys once; we seal them locally for seal/open.
    if (!self && !priv) {
      try {
        const prov = await crypto.provisionIdentity(email);
        if (prov?.privateKeys) { priv = prov.privateKeys; keystore.saveIdentity(email, priv); }
        self = await crypto.lookupRecipient(email).catch(() => null);
      } catch (e) { console.warn('[provision] auto-provision failed:', e.message); }
    }
    // identity shape expected by Compose/MessageView: { kem:{privateKey}, sig:{privateKey} }.
    const identity = priv ? { kem: { privateKey: priv.kem }, sig: { privateKey: priv.sig } } : null;
    return { jmap, crypto, email, apis, keystore, identity, fingerprint: self?.fingerprint || null };
  }, [keystore]);

  const handleLogin = useCallback(async ({ email, password, remember }) => {
    setError(null);
    try {
      const authHeader = JmapClient.basicAuth(email, password);
      // If 2FA is enabled, validate the password first, then require an authenticator code.
      if (await totpRequired(email)) {
        await new JmapClient({ baseUrl: '', authHeader }).connect(); // throws on bad password
        setTotpStep({ email, authHeader, remember });
        return;
      }
      const s = await buildSession(email, authHeader);
      SESSION.save({ email, authHeader }, remember);
      setSession(s);
      flash(s.fingerprint ? 'Keys unlocked locally' : 'Signed in');
    } catch (e) {
      setError(e.message || 'Login failed');
    }
  }, [flash, buildSession]);

  const submitTotp = useCallback(async (code) => {
    if (!totpStep) return;
    setError(null);
    try {
      if (!(await totpVerify(totpStep.email, code))) { setError('Invalid authenticator code'); return; }
      const s = await buildSession(totpStep.email, totpStep.authHeader);
      SESSION.save({ email: totpStep.email, authHeader: totpStep.authHeader }, totpStep.remember);
      setSession(s); setTotpStep(null);
      flash('Signed in');
    } catch (e) { setError(e.message || 'Login failed'); }
  }, [totpStep, buildSession, flash]);

  // Restore a persisted session on load so a refresh does NOT log the user out.
  useEffect(() => {
    const saved = SESSION.load();
    if (!saved) { setRestoring(false); return; }
    let cancelled = false;
    buildSession(saved.email, saved.authHeader)
      .then((s) => { if (!cancelled) setSession(s); })
      .catch(() => { SESSION.clear(); }) // stale/invalid creds -> drop silently
      .finally(() => { if (!cancelled) setRestoring(false); });
    return () => { cancelled = true; };
  }, [buildSession]);

  const handleRegistered = useCallback(async ({ email, password, phrase }) => {
    await keystore.create(email, { password, phrase });
    await handleLogin({ email, password, remember: true });
  }, [handleLogin, keystore]);

  const handleLogout = useCallback(() => { SESSION.clear(); setSession(null); setScreen({ name: 'login', email: '' }); }, []);

  if (session) return <Shell session={session} theme={theme} onLogout={handleLogout} />;

  // Restoring a saved session: show a splash, never a flash of the login page.
  if (restoring) return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--km-bg)' }}>
      <img src="/brand/mark.svg" width="44" height="44" alt="KryptaMail" style={{ opacity: 0.9, animation: 'km-pulse 1.1s ease-in-out infinite' }} />
    </div>
  );

  // Second login factor: enter the authenticator code before the session is built.
  if (totpStep) return (
    <AuthShell theme={theme}>
      <TotpGate email={totpStep.email} error={error} onSubmit={submitTotp} onCancel={() => { setTotpStep(null); setError(null); }} />
    </AuthShell>
  );

  return (
    <AuthShell theme={theme}>
      {screen.name === 'login' && <Login onLogin={handleLogin} error={error} initialEmail={screen.email} go={go} onPasskey={() => flash('Passkey sign-in: wire to WebAuthn')} />}
      {screen.name === 'register' && <Register api={recoveryApi} crypto={new CryptoClient()} registration={registration} onDone={handleRegistered} go={go} />}
      {screen.name === 'forgot' && <Forgot api={recoveryApi} initialEmail={screen.email} go={go} />}
      {screen.name === 'find' && <FindAddress api={recoveryApi} go={go} />}
    </AuthShell>
  );
}
